/**
 * Browser storage telemetry for the local-first viewer.
 *
 * The viewer base never uploads bytes to a server, so all persistent
 * state (viewpoints + their JPEG thumbnails, BCF time-machine snapshots
 * if the BCF module is active, FS Access handles, etc.) lives in the
 * browser's storage subsystem, primarily IndexedDB. The browser
 * imposes a per-origin quota: a fraction of free disk (Chrome ~60%,
 * Firefox ~50%, Safari minimum a few hundred MB and asks the user
 * past that). We expose:
 *
 *  • estimateOriginStorage(): total bytes used + quota for THIS origin
 *    via navigator.storage.estimate(). Covers IDB, localStorage,
 *    Caches, OPFS: everything our origin holds.
 *
 *  • requestPersistent(): ask the browser to mark our storage as
 *    "persistent" (resists eviction under storage pressure). On
 *    Chrome it's silent and based on user engagement; on Firefox it
 *    shows a permission prompt. Already-persistent storage returns
 *    true immediately. Worth offering when the user is approaching
 *    the soft limits.
 *
 *  • formatBytes(): tight human-readable formatter (1.4 MB, 240 KB).
 */

export interface OriginStorageEstimate {
  usageBytes: number | null
  quotaBytes: number | null
  /** Best-effort breakdown if the browser supports it. */
  breakdown?: Record<string, number>
}

export async function estimateOriginStorage(): Promise<OriginStorageEstimate> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.storage ||
    typeof navigator.storage.estimate !== 'function'
  ) {
    return { usageBytes: null, quotaBytes: null }
  }
  try {
    const est = await navigator.storage.estimate()
    const breakdown = (est as StorageEstimate & {
      usageDetails?: Record<string, number>
    }).usageDetails
    return {
      usageBytes: typeof est.usage === 'number' ? est.usage : null,
      quotaBytes: typeof est.quota === 'number' ? est.quota : null,
      breakdown,
    }
  } catch {
    return { usageBytes: null, quotaBytes: null }
  }
}

export async function isStoragePersistent(): Promise<boolean> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.storage ||
    typeof navigator.storage.persisted !== 'function'
  ) {
    return false
  }
  try {
    return await navigator.storage.persisted()
  } catch {
    return false
  }
}

export async function requestPersistent(): Promise<boolean> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.storage ||
    typeof navigator.storage.persist !== 'function'
  ) {
    return false
  }
  try {
    return await navigator.storage.persist()
  } catch {
    return false
  }
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '-'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
